import { Link } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";
import SectionHeading from "@/components/base/SectionHeading";
import NewsCard from "@/components/base/NewsCard";
import WorkCard from "@/components/base/WorkCard";
import HeroCarousel from "./components/HeroCarousel";
import { newsList } from "@/mocks/news";
import { worksList } from "@/mocks/works";
import { topicList } from "@/mocks/topics";

export default function Home() {
  usePageMeta({
    title: "漫蛙官网 - 最新动漫资讯与精彩作品展示",
    description:
      "漫蛙官网为动漫爱好者提供丰富的动漫资讯与精彩作品展示，涵盖多种动漫类型和最新动态，助您畅享动漫世界。",
  });

  const latestNews = [...newsList]
    .sort((a, b) => (a.date < b.date ? 1 : -1))
    .slice(0, 5);

  const hotWorks = [...worksList].sort((a, b) => b.heat - a.heat).slice(0, 6);

  return (
    <div className="w-full">
      <HeroCarousel />

      {/* Category entries */}
      <section className="w-full px-4 md:px-8 -mt-10 md:-mt-14 relative z-20">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          <Link
            to="/news"
            className="group flex items-center gap-4 md:gap-6 p-5 md:p-7 rounded-lg bg-background-50 border border-background-200 hover:border-primary-300 transition-colors cursor-pointer"
          >
            <span className="w-14 h-14 md:w-16 md:h-16 shrink-0 rounded-lg bg-primary-500 text-background-50 flex items-center justify-center">
              <i className="ri-newspaper-line text-2xl md:text-3xl"></i>
            </span>
            <div className="min-w-0">
              <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950 group-hover:text-primary-700 transition-colors">
                动漫资讯
              </h2>
              <p className="text-sm text-foreground-600 mt-1">
                行业动态 · 新番介绍 · 活动预告 · 深度评测
              </p>
            </div>
            <i className="ri-arrow-right-line text-2xl text-foreground-400 ml-auto group-hover:text-primary-600 group-hover:translate-x-1 transition-all"></i>
          </Link>

          <Link
            to="/works"
            className="group flex items-center gap-4 md:gap-6 p-5 md:p-7 rounded-lg bg-background-50 border border-background-200 hover:border-accent-300 transition-colors cursor-pointer"
          >
            <span className="w-14 h-14 md:w-16 md:h-16 shrink-0 rounded-lg bg-accent-500 text-background-50 flex items-center justify-center">
              <i className="ri-gallery-view-2 text-2xl md:text-3xl"></i>
            </span>
            <div className="min-w-0">
              <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950 group-hover:text-accent-700 transition-colors">
                动漫作品
              </h2>
              <p className="text-sm text-foreground-600 mt-1">
                按类型 / 地区 / 年代，发现你的下一部心爱之作
              </p>
            </div>
            <i className="ri-arrow-right-line text-2xl text-foreground-400 ml-auto group-hover:text-accent-600 group-hover:translate-x-1 transition-all"></i>
          </Link>
        </div>
      </section>

      {/* Latest news */}
      <section className="w-full px-4 md:px-8 py-12 md:py-16">
        <SectionHeading
          title="最新资讯"
          subtitle="按发布时间倒序，第一时间掌握动漫圈的最新动态"
          moreTo="/news"
        />
        <div className="grid grid-cols-1 gap-4 md:gap-5">
          {latestNews.map((item) => (
            <NewsCard key={item.id} item={item} />
          ))}
        </div>
      </section>

      {/* Hot works */}
      <section className="w-full px-4 md:px-8 py-12 md:py-16 bg-background-100">
        <SectionHeading
          title="热门作品"
          subtitle="精选高人气动漫作品，覆盖动画、漫画与剧场版"
          moreTo="/works"
        />
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-5">
          {hotWorks.map((work) => (
            <WorkCard key={work.id} work={work} />
          ))}
        </div>
      </section>

      {/* Topics */}
      <section className="w-full px-4 md:px-8 py-12 md:py-16">
        <SectionHeading
          title="推荐专题合集"
          subtitle="按主题精选作品，帮你更快找到对味的内容"
          moreTo="/works"
          moreLabel="更多合集"
        />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          {topicList.map((topic) => (
            <Link
              key={topic.id}
              to="/works"
              className="group block rounded-lg overflow-hidden bg-background-50 border border-background-200 hover:border-primary-300 transition-colors cursor-pointer"
            >
              <div className="relative w-full aspect-[16/10] overflow-hidden bg-background-100">
                <img
                  src={topic.cover}
                  alt={topic.title}
                  title={`${topic.title} 动漫主题合集`}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="p-4 md:p-5">
                <h3 className="font-heading font-bold text-foreground-950 text-base md:text-lg group-hover:text-primary-700 transition-colors">
                  {topic.title}
                </h3>
                <p className="text-sm text-foreground-600 mt-2 leading-relaxed">
                  {topic.intro}
                </p>
                <span className="inline-flex items-center gap-1 text-sm text-primary-700 font-medium mt-3">
                  浏览合集
                  <i className="ri-arrow-right-s-line text-base"></i>
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Intro */}
      <section className="w-full px-4 md:px-8 pb-16 md:pb-20">
        <div className="rounded-lg bg-secondary-50 border border-secondary-200 p-6 md:p-10">
          <div className="flex flex-col lg:flex-row items-start lg:items-center gap-6 lg:gap-10">
            <div className="w-14 h-14 md:w-16 md:h-16 shrink-0 rounded-lg bg-secondary-500 text-background-50 flex items-center justify-center">
              <i className="ri-leaf-line text-2xl md:text-3xl"></i>
            </div>
            <div className="flex-1">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                关于漫蛙官网
              </h2>
              <p className="text-sm md:text-base text-foreground-700 mt-3 leading-relaxed max-w-4xl">
                漫蛙官网聚焦动漫领域，围绕「动漫资讯」与「动漫作品」双线运营，
                为喜欢动画、漫画与二次元文化的用户提供最新动态、详尽的作品介绍与分类浏览。
                界面清晰、更新及时，方便你探索、收藏并持续跟进感兴趣的内容。
              </p>
            </div>
            <Link
              to="/about"
              className="shrink-0 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md bg-secondary-500 hover:bg-secondary-600 text-background-50 text-sm font-medium whitespace-nowrap transition-colors cursor-pointer"
            >
              了解更多
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}